import { LazyLoadingSectionLogger, LoggingSection } from "Logging/index";

const logger = LazyLoadingSectionLogger.create(LoggingSection.customElements);

/**
     * Definice nového objektu pro práci s Custom Elementy podle https://html.spec.whatwg.org/multipage/scripting.html#custom-elements
     */
export interface CustomElementRegistry {
    define(name: string, constructor: Function, options?: ElementDefinitionOptions): void;
    get(name: string): typeof HTMLElement;
    whenDefined(name: string): Promise<void>;
}

export interface ElementDefinitionOptions {
    extends?: string;
}

/**
 * Hledá první rodičovský element předaného elementu, který odpovídá zadanému predikátu
 * @param startElement Element, kterého rodiče se mají prohledávat
 * @param comparePredicate Porovnávací predikát nebo selector, první vyhovující rodič je vrácen nebo null
 */
export function findParent(startElement: HTMLElement, comparePredicate: string | ((el: HTMLElement) => boolean)): HTMLElement | null {
    if (startElement.parentElement == null)
        return null;

    if (typeof comparePredicate === "string") {
        if (startElement.parentElement.matches(comparePredicate))
            return startElement.parentElement;
    } else {
        if (comparePredicate(startElement.parentElement))
            return startElement.parentElement;
    }

    return findParent(startElement.parentElement, comparePredicate);
}

/**
 * Hledá, zda existuje nějaký rodičovský element předaného elementu, který odpovídá zadanému predikátu
 * @param startElement Element, kterého rodiče se mají prohledávat
 * @param comparePredicate Porovnávací predikát nebo selector
 */
export function hasParent(startElement: HTMLElement, comparePredicate: string | ((el: HTMLElement) => boolean)): boolean {
    return findParent(startElement, comparePredicate) != null;
}

/**
 * Kontroluje, zda všechny rodičovské elementy předaného elementu odpovídají zadanému predikátu
 * @param startElement Element, kterého rodiče se mají prohledávat
 * @param comparePredicate Porovnávací predikát nebo selector
 * @param checkStartElement Kontrola začíná už na předaném startElement-u
 */
export function allParents(startElement: HTMLElement | null, comparePredicate: string | ((el: HTMLElement) => boolean), checkStartElement: boolean = true): boolean {
    const elementForCheck = checkStartElement ? startElement : startElement?.parentElement;

    if (elementForCheck == null)
        return true;

    if (typeof comparePredicate === "string") {
        if (!elementForCheck.matches(comparePredicate))
            return false;
    } else {
        if (!comparePredicate(elementForCheck))
            return false;
    }

    return allParents(elementForCheck.parentElement, comparePredicate, checkStartElement);
}

export function customElementRegistry(): CustomElementRegistry {
    return (window as any).customElements as CustomElementRegistry;
}

/**
 * Zaregistruje nový Custom Element
 * @param name název nového elementu - musí obsahovat pomlčku
 * @param classObject třída nového elementu
 * @param doNotUseDeprecatedExtend ODSTRANĚNO ZE SPECIFIKACE - NEPOUŽÍVAT!!!
 * @includeToDoc
 */
export function registerCustomElement(name: string, classObject: typeof HTMLElement, doNotUseDeprecatedExtend?: string): void {
    if (customElementRegistry().get(name)) {
        logger.warn(`Cannot register custom element. '${name}' is already used.`);//`
        return;
    }
    name = name.toLowerCase();

    let options: ElementDefinitionOptions | undefined; // buď musí být undefined nebo objekt s property extends
    if (doNotUseDeprecatedExtend) {
        options = {
            extends: doNotUseDeprecatedExtend,
        };
        //logger.debug(`WARN: Using deprecated feature 'customized built-in elements'. Custom element '${name}' of class '${classObject.name}' is extending built-in element '${doNotUseDeprecatedExtend}'`);
    }

    try {
        customElementRegistry().define(name, classObject, options);
    } catch (e) {
        logger.warn(e);
    }
}

/**
 * Nakonfiguruje předaný element podle zadaných parametrů
 * @param el html element, který je určen ke konfiguraci
 * @param settings konfigurační parametry
 */
export function configureElement<T extends HTMLElement>(el: T, settings: ICreateElement = {}): T {
    if (settings == undefined)
        return el as T;

    settings.el = el;
    return createElement(settings) as T;
}

/**
 * Vytvoří element dle zadaných parametrů
 *
 * Parametry:
 * type(string) = typ elementu napr. div,input,span..., vychozi hodnota je div
 * classes(string|string[]) = muze obsahovat string a nebo pole stringu, ktere predstavuji nazvy prirazovanych trid
 * id(string) = id elementu
 * value(string) = pridava textovou hodnotu v zavislosti na typu elementu pokud je to input predava jako value, pokud img, video, iframe nebo source tak jako src jinak jako textContent
 * html(string) = predava hodnotu elementu jako HTML do innerHTML
 * attrs(object) = predavan jako objekt, ktery obsahuje nazev attributu a jeho hodnotu {"data-test": 1, title: "ahoj"}
 * css(object) = nastaveni elementu style {display: "none", borderRadius: 5 }
 * @includeToDoc
 */
export function createElement(setting: ICreateElement = {}): HTMLElement {
    if (setting == undefined)
        return document.createElement("div");

    let d: HTMLElement;
    let ty_input = "";

    if (setting.el) { // pokud je předaný už vytvořený element, tak se použije ten a nevytváří se nový podle type
        d = setting.el;
        if (d instanceof HTMLInputElement)
            ty_input = d.type;
    } else {
        if (setting.type) {
            setting.type = setting.type.toLowerCase();

            if (setting.type.startsWith("input[")) {
                const fullType = setting.type.substring(6);
                setting.type = "input";
                ty_input = fullType.substring(0, fullType.length - 1);
            }
        } else {
            setting.type = "div";
        }

        if (setting.type.indexOf("-") >= 0) {
            const ctor = customElementRegistry().get(setting.type);
            if (!ctor)
                throw new Error(`Definition for ${setting.type} not found!`);
            d = new ctor();
        } else {
            d = document.createElement(setting.type);
        }
    }

    if (setting.classes != undefined) {
        if (setting.classes instanceof Array) {
            for (const cls of setting.classes) {
                if (cls)
                    d.classList.add(cls);
            }
        } else {
            //d.classList.add(setting.classes.toString());
            d.className = (d.className ? d.className + " " : "") + setting.classes.toString();
        }
    }
    if (setting.id != undefined) {
        d.setAttribute("id", setting.id);
    }

    if (ty_input.length > 0 && setting.type === "input") {
        (d as HTMLInputElement).type = ty_input;
    }

    if (setting.value != undefined) {
        if (setting.type === "input") {
            if (ty_input == "checkbox")
                (d as HTMLInputElement).checked = setting.value;
            else
                (d as HTMLInputElement).value = setting.value;
        } else if (setting.type == "iframe" || setting.type == "img" || setting.type == "video" || setting.type == "source" || setting.type == "tsc-formiframe") {
            (d as HTMLSourceElement).src = setting.value;
        } else if (setting.type == "option") {
            (d as HTMLInputElement).value = setting.value;
        } else
            d.textContent = setting.value;
    }

    if (setting.html != undefined) {
        d.innerHTML = setting.html;
    }

    if (setting.css != undefined) {
        const ok = Object.keys(setting.css) as any[];

        for (const attr of ok) {
            if (d.style[attr] != undefined) {
                d.style[attr] = setting.css[attr].toString();
            }
        }
    }

    if (setting.attrs != undefined) {
        const ok = Object.keys(setting.attrs);
        for (const attr of ok) {
            if (setting.attrs[attr] === null)
                d.setAttribute(attr, "null");
            else if (setting.attrs[attr] === undefined)
                continue;
            else
                d.setAttribute(attr, setting.attrs[attr].toString());
        }
    }

    return d;
}

/**
 * @includeToDoc
 */
export interface ICreateElement {
    /**
     * předvytvořený element, pokud existuje, tak už se nepoužije type atribut
     */
    el?: HTMLElement;
    /**
     * typ elementu (span, div, etc..), default: div
     */
    type?: string;
    /**
     * string a nebo pole stringu, ktere predstavuji nazvy trid, ktere se maji nastavit elementu
     */
    classes?: string | string[];
    /**
     * id elementu
     */
    id?: string;
    /**
     * pridavana textova hodnota v zavislosti na typu elementu:
     * input - nastavuje se do value
     * img, video, iframe nebo source - nastavuje se do src
     * jinak - do textContent
     */
    value?: any;
    /**
     * pridavana hodnotu elementu jako HTML do innerHTML
     */
    html?: string;
    /**
     * seznam hodnot stylu, napr. {display: "none", borderRadius: 5 }
     */
    css?: { [attrNam: string]: any };
    /**
     * objekt, ktery obsahuje nazev attributu a jeho hodnotu, napr. {"data-test": 1, title: "ahoj"}
     */
    attrs?: { [attrNam: string]: string };
}

export function getScrollBarWidth() {
    const inner = document.createElement("p");
    inner.style.width = "100%";
    inner.style.height = "200px";

    const outer = document.createElement("div");
    outer.style.position = "absolute";
    outer.style.top = "0px";
    outer.style.left = "0px";
    outer.style.visibility = "hidden";
    outer.style.width = "200px";
    outer.style.height = "150px";
    outer.style.overflow = "hidden";
    outer.appendChild(inner);

    document.body.appendChild(outer);
    const w1 = inner.offsetWidth;
    outer.style.overflow = "scroll";
    let w2 = inner.offsetWidth;
    if (w1 == w2)
        w2 = outer.clientWidth;

    document.body.removeChild(outer);

    return (w1 - w2);
}
